import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	type WorkflowPublicationStatus,
	LEGACY_EDITOR,
	NEW_EDITOR,
} from './WorkflowPublicationStatus.types.tsx';

const SET_GLOBAL_SCOPE_USER_PREFERENCE_MUTATION = `
    mutation SetGlobalScopeUserPreferenceMutation($input: SetMyPreferenceInput!) {
        setMyPreference(input: $input) {
            success
        }
    }
`;

const getAnalyticsStatusName = (status: WorkflowPublicationStatus): string => {
	switch (status) {
		case 'DIRTY':
			return 'Unsaved'; // user-friendly term to use instead of Dirty
		case 'DRAFT':
			return 'Draft';
		case 'LIVE':
			return 'Live';
		case 'NONE':
			return 'None';
		default:
			return status;
	}
};

const setGlobalScopeUserPreference = async (input: { key: string; value: string }) => {
	const result = await performPostRequest('/rest/gira/1/', {
		body: JSON.stringify({
			query: SET_GLOBAL_SCOPE_USER_PREFERENCE_MUTATION,
			variables: {
				input,
			},
		}),
	});

	if (result.data.setMyPreference.success !== true) {
		throw new Error(`Error setting user preference: ${input.key}`, {
			cause: result.errors?.[0] ?? 'unknown',
		});
	}
};

export const changeDefaultWorkflowEditorToLegacy = async (
	analyticsEvent: UIAnalyticsEvent,
	workflowPublicationStatus: WorkflowPublicationStatus,
): Promise<void> => {
	try {
		await setGlobalScopeUserPreference({
			key: 'jira.user.workflow.editor.global.scope.enrolment',
			value: LEGACY_EDITOR,
		});

		fireTrackAnalytics(analyticsEvent, 'legacyWorkflowEditorUserPreference set', {
			workflowPublicationStatus: getAnalyticsStatusName(workflowPublicationStatus),
		});
	} catch (error) {
		fireErrorAnalytics({
			meta: {
				id: 'changeDefaultWorkflowEditorToLegacy',
				packageName: 'jiraWorkflowEditorPreferenceSettings',
			},
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			error: error as Error,
		});

		throw error;
	}
};

export const changeDefaultWorkflowEditorToNew = async (
	analyticsEvent: UIAnalyticsEvent,
	workflowPublicationStatus: WorkflowPublicationStatus,
): Promise<void> => {
	try {
		await setGlobalScopeUserPreference({
			key: 'jira.user.workflow.editor.global.scope.enrolment',
			value: NEW_EDITOR,
		});

		fireTrackAnalytics(analyticsEvent, 'newWorkflowEditorUserPreference set', {
			workflowPublicationStatus: getAnalyticsStatusName(workflowPublicationStatus),
		});
	} catch (error) {
		fireErrorAnalytics({
			meta: {
				id: 'changeDefaultWorkflowEditorToNew',
				packageName: 'jiraWorkflowEditorPreferenceSettings',
			},
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			error: error as Error,
		});

		throw error;
	}
};
